"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useId, useState, type FormEvent } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import type { AuthMode } from "./authMode"

interface ForgotPasswordFormProps {
  initialEmail: string
  onModeChange: (mode: AuthMode, email?: string) => void
}

export function ForgotPasswordForm({ initialEmail, onModeChange }: ForgotPasswordFormProps) {
  const { t } = useI18n()
  const { forgotPassword, errorKey, clearError } = useAuth()
  const [email, setEmail] = useState(initialEmail)
  const [submitting, setSubmitting] = useState(false)
  const emailId = useId()

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    clearError()
    setSubmitting(true)
    try {
      await forgotPassword(email)
      onModeChange("reset", email)
    } catch {
      // errorKey is already set by the auth context.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
      <p className="text-sm text-ink-2">{t("auth.forgotIntro")}</p>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={emailId} className="text-sm font-medium text-ink">
          {t("auth.emailLabel")}
        </label>
        <Input
          id={emailId}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={event => setEmail(event.target.value)}
        />
      </div>

      {errorKey && (
        <p role="alert" className="text-sm text-destructive">
          {t(errorKey)}
        </p>
      )}

      <Button type="submit" disabled={submitting} className="min-h-11">
        {t("auth.sendResetCode")}
      </Button>

      <button
        type="button"
        className="text-center text-sm text-muted-foreground underline underline-offset-2"
        onClick={() => onModeChange("signIn")}
      >
        {t("auth.backToSignIn")}
      </button>
    </form>
  )
}
